import { useCallback, useState } from 'react';
import { BiPlus } from 'react-icons/bi';

import { ButtonSmall } from '@/components/Button';
import { ConfirmationModal } from '@/components/ConfirmationModal';
import CreateEditTaskSheet, {
  type CreateTaskPayload,
} from '@/components/CreateSheet/CreateEditTaskSheet';
import { MaintenanceTaskCard } from '@/components/Widget/MaintenanceWidget/MaintenanceTaskCard';

import s from './TasksSection.module.scss';

export type TaskItemType = CreateTaskPayload;

type Props = {
  tasks?: TaskItemType[];
  onChange?: (tasks: TaskItemType[]) => void;
};

function TasksSection({ tasks = [], onChange }: Props) {
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [isTaskSheetOpen, setIsTaskSheetOpen] = useState(false);
  const [taskToDelete, setTaskToDelete] = useState<string | null>(null);

  const handleEdit = (taskId: string) => {
    const index = tasks.findIndex((t) => t.id === taskId);
    if (index !== -1) {
      setEditingIndex(index);
      setIsTaskSheetOpen(true);
    }
  };

  const confirmDelete = () => {
    if (taskToDelete && onChange) {
      onChange(tasks.filter((t) => t.id !== taskToDelete));
    }
    setTaskToDelete(null);
  };

  const handleAddClick = () => {
    setEditingIndex(null);
    setIsTaskSheetOpen(true);
  };

  const handleSubmitTask = useCallback(
    async (payload: CreateTaskPayload) => {
      const taskWithId = {
        ...payload,
        id: payload.id || `temp-${Date.now()}-${Math.random()}`,
      };

      if (editingIndex !== null) {
        const newTasks = [...tasks];
        // Merge over the original so preloaded backend fields survive the edit
        newTasks[editingIndex] = { ...tasks[editingIndex], ...taskWithId };
        onChange?.(newTasks);
      } else {
        onChange?.([...tasks, taskWithId]);
      }
      setIsTaskSheetOpen(false);
      setEditingIndex(null);
    },
    [editingIndex, tasks, onChange],
  );

  return (
    <section>
      <div className={s.sectionTitle}>
        <span>Tasks</span>
      </div>
      {tasks.length > 0 && (
        <div className={s.taskList}>
          {tasks
            .sort((a, b) => new Date(a.start ?? 0).getTime() - new Date(b.start ?? 0).getTime())
            .map((task) => (
              <MaintenanceTaskCard
                key={task.id}
                task={task as any}
                hideStatusBadge
                onEdit={handleEdit}
                onDelete={setTaskToDelete}
              />
            ))}
        </div>
      )}
      <button type="button" className={s.addTaskButton} onClick={handleAddClick}>
        <ButtonSmall type="button" className={s.addButton}>
          <BiPlus size={24} />
        </ButtonSmall>
        Add Task
      </button>

      {isTaskSheetOpen && (
        <CreateEditTaskSheet
          open={isTaskSheetOpen}
          onClose={() => {
            setIsTaskSheetOpen(false);
            setEditingIndex(null);
          }}
          onSubmitTask={handleSubmitTask}
          title={editingIndex !== null ? 'Edit Task' : 'Add Task'}
          initialData={editingIndex !== null ? (tasks[editingIndex] as any) : undefined}
          hideRepeat
          hideWorkflowStatuses
        />
      )}

      <ConfirmationModal
        open={!!taskToDelete}
        onClose={() => setTaskToDelete(null)}
        onConfirm={confirmDelete}
        title="Delete Task"
        message="Are you sure you want to delete this task?"
      />
    </section>
  );
}

export default TasksSection;
